import {
	ActionList,
	ActionListGroup,
	ActionListItem,
	Alert,
	AlertActionLink,
	Button,
	Content,
	ContentVariants,
	Flex,
	FlexItem,
	Form,
	FormGroup,
	Icon,
	Label,
	Radio,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import { CheckCircleIcon, OutlinedQuestionCircleIcon } from "@patternfly/react-icons";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";
import { ErrorPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { useControllerContext } from "@rhc-shared-components/control-wrapper";
import type { IRadioItem } from "@rhc-shared-components/form-radio-select";
import {
	type PartnerViewConfig,
	Questionnaire,
	type QuestionnaireResponse,
} from "@rhc-shared-components/questionnaire";
import { ToolTip } from "@rhc-shared-components/tool-tip";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { type FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { patchProductListing } from "../../api/pyxis";
import {
	EPartnerValidationProgress,
	type EWorkloadType,
	OpenShiftFullName,
	ProductKeys,
	ProductListingTypes,
	RHELFullName,
} from "../../constants/product";
import {
	ContainerValidationTypeOptions,
	OPENSHIFT_CONTAINER_FULLNAME_REGEX,
	StandaloneValidationTypeOptions,
	SUPPORTED_PLATFORMS_MAX_ROWS,
	WorkloadTypeQuestionnaireAlias,
} from "../../constants/validation";
import { useProductMetadata } from "../../hooks/useProductMetadata";
import type { IPyxisAPIError } from "../../interfaces/pyxis";
import {
	invalidateProductQueries,
	productDetailQueryKey,
	useRedhatProductVersions,
} from "../../queries/productDetails";
import type { ProductListing } from "../../types/generated-graphql";
import { validateAttachedProjects } from "../../utils/attachedProjectValidator";
import Env from "../../utils/env";
import { getAuthToken } from "../../utils/fetch";
import { sortVersionsDescending } from "../../utils/sortVersions";
import { ProductVersionModal } from "../ProductInfo/ProductVersionMatrix/ProductVersionModal";
import type { ICertAndValidationProps } from "./common-interfaces";

interface SupportStatementProps extends ICertAndValidationProps {
	navigateToComponentTesting: () => void;
}

const VALIDATION_TYPE_FIELD = "validationType";

const SupportStatement: FC<SupportStatementProps> = ({ onNext, navigateToComponentTesting }) => {
	const { isReadOnly } = useControllerContext();
	const [productVersionModalState, setProductVersionModalState] = useState<{
		isVisible: boolean;
		productVersionId?: string;
	}>({ isVisible: false });
	const [, setFormStatus] = useState("Invalid");
	const [selectedWorkloadType, setSelectedWorkloadType] = useState<EWorkloadType | null>(null);
	const [questionnaireResponseData, setQuestionnaireResponseData] = useState<QuestionnaireResponse | null>(null);
	const {
		productDetail,
		activeCertificationListState,
		partnerValidationProgress,
		productVersionsData,
		combinedVersionsData,
		componentMetadata: { isCertifiedProduct },
		globalDisabledState,
	} = useProductMetadata();
	const isProductCertified =
		validateAttachedProjects(productDetail, activeCertificationListState, false) && isCertifiedProduct;
	const isRhelFutureValidated = useMemo(
		() =>
			productVersionsData.some((entry) =>
				entry.redhat_affinity?.some(
					(affinity) => affinity.product_name === RHELFullName && affinity.support_future_versions,
				),
			),
		[productVersionsData],
	);
	const { data: rhProductVersions } = useRedhatProductVersions();
	const queryClient = useQueryClient();
	const { setAlertState } = useAlertContext();
	const updateProductListingMutation = useMutation({
		mutationFn: (request: Partial<ProductListing>) => patchProductListing(request),
	});
	const RHOCPFutureVersionCount = useMemo(() => {
		if (!rhProductVersions) return 0;
		const allRhocpVersions = rhProductVersions.filter((v) => v?.redHatProduct?.shortName === "RHOCP");
		const byMajor: Record<string, typeof allRhocpVersions> = {};
		for (const v of allRhocpVersions) {
			const major = v.name.split(".")[0];
			if (!byMajor[major]) byMajor[major] = [];
			byMajor[major].push(v);
		}
		const futureNames = new Set<string>();
		for (const versions of Object.values(byMajor)) {
			const firstGAIndex = versions.findIndex((v) => v.isPublic && v.isSupport);
			if (firstGAIndex > 0) {
				versions
					.slice(0, firstGAIndex)
					.filter((v) => !v.isPublic && !v.isSupport)
					.slice(0, 3)
					.forEach((v) => {
						futureNames.add(v.name);
					});
			}
		}
		const savedRHOCPVersions = new Set<string>();
		for (const entry of productVersionsData) {
			for (const affinity of entry.redhat_affinity ?? []) {
				if (affinity.product_name === OpenShiftFullName) {
					for (const v of affinity.versions ?? []) savedRHOCPVersions.add(v);
				}
			}
		}
		return [...savedRHOCPVersions].filter((v) => futureNames.has(v)).length;
	}, [rhProductVersions, productVersionsData]);
	const rhelVersionsSupported = useMemo(() => {
		const versions = new Set<string>();
		for (const entry of productVersionsData) {
			for (const affinity of entry.redhat_affinity ?? []) {
				if (affinity.product_name === RHELFullName) {
					for (const v of affinity.versions ?? []) versions.add(v);
				}
			}
		}
		return sortVersionsDescending([...versions]);
	}, [productVersionsData]);
	const rhOCPVersionsSupported = useMemo(() => {
		const versions = new Set<string>();
		for (const entry of productVersionsData) {
			for (const affinity of entry.redhat_affinity ?? []) {
				if (affinity.product_name === OpenShiftFullName) {
					for (const v of affinity.versions ?? []) versions.add(v);
				}
			}
		}
		return sortVersionsDescending([...versions]);
	}, [productVersionsData]);
	const handleProductVersionModal = (isVisible: boolean) => {
		setProductVersionModalState({ isVisible });
	};

	const versionMatrixLink = `/manage/products/${productDetail._id}/product-information/version-matrix`;
	const hasVersionMatrixData = combinedVersionsData?.length > 0;

	const hasOverflowRows = productVersionsData.length > SUPPORTED_PLATFORMS_MAX_ROWS;
	const persistedWorkloadType = productDetail.product_validation?.workload_type?.[0];
	const handleQuestionnaireResponse = (existingQuestionnaireResponse: QuestionnaireResponse) => {
		setQuestionnaireResponseData(existingQuestionnaireResponse);
	};
	const handleQuestionnaireSubmit = () => {
		if (persistedWorkloadType) return;

		updateProductListingMutation.mutate(
			{
				[ProductKeys.id]: productDetail._id,
				product_validation: { workload_type: [selectedWorkloadType] },
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: productDetailQueryKey(productDetail._id || "") });
					invalidateProductQueries(queryClient, productDetail._id || "");
				},
				onError: ({ detail }: IPyxisAPIError) => {
					ErrorPopup(setAlertState, detail || "Something went wrong while updating product validation");
				},
			},
		);
	};

	const renderQuestionnaireSection = (alias: string) => (
		<>
			{questionnaireResponseData?.status === "requested" && (
				<StackItem>
					<Alert
						variant="warning"
						title={
							"Your Software Validation Questionnaire responses are currently under review, you will receive an email notification when the review is completed."
						}
					/>
				</StackItem>
			)}
			<StackItem>
				<Questionnaire
					config={
						{
							mode: "partner",
							alias,
							uniqueEntityId: productDetail._id,
							env: Env.getEnvName(),
							showBuiltInSubmitButton: true,
							hideSubmitError: false,
							setStatus: setFormStatus,
							getAuthToken,
							onExistingSubmission: (response: QuestionnaireResponse) => handleQuestionnaireResponse(response),
							onQuestionnaireSubmit: () => handleQuestionnaireSubmit(),
							isReadMode: isReadOnly,
						} as PartnerViewConfig
					}
				/>
			</StackItem>
		</>
	);

	const vendorMatrixTable = () => (
		<>
			{partnerValidationProgress === EPartnerValidationProgress.Started && (
				<StackItem>
					<Alert
						variant="info"
						isInline
						title="Submit the questionnaire to finish your validation. Validation completes after Red Hat approval."
					>
						To manage what versions this product claims to support, visit the{" "}
						<Button isInline variant="link" component={(props) => <Link {...props} to={versionMatrixLink} />}>
							version matrix
						</Button>{" "}
						for your product.
					</Alert>
				</StackItem>
			)}
			<StackItem>
				<Content>
					<Content component="h5">Supported platforms</Content>
				</Content>
				<Table aria-label="Supported platforms">
					<Thead>
						<Tr>
							<Th>Partner Product version</Th>
							<Th>Red Hat platform(s) supported</Th>
						</Tr>
					</Thead>
					<Tbody>
						{combinedVersionsData.slice(0, 5).map((entry, index) => (
							<Tr key={`${entry.product_version}-${index}`}>
								<Td dataLabel="Partner Product version">
									<Flex spaceItems={{ default: "spaceItemsMd" }} alignItems={{ default: "alignItemsCenter" }}>
										<FlexItem>{entry?.product_version || "-"}</FlexItem>
										{entry?.certificationLevel === "Certified" && (
											<FlexItem>
												<Label color="green" isCompact>
													Certified
												</Label>
											</FlexItem>
										)}
									</Flex>
								</Td>
								<Td dataLabel="Red Hat platform(s) supported">
									<Content component="ul">
										{entry.redhat_affinity?.map((affinity) => {
											const name = affinity.product_name.replace(
												OPENSHIFT_CONTAINER_FULLNAME_REGEX,
												"Red Hat OpenShift",
											);
											const versions = sortVersionsDescending([...(affinity.versions || [])]);
											return (
												<Content component="li" key={affinity.product_name}>
													{name} {versions.join(", ")}
												</Content>
											);
										})}
									</Content>
								</Td>
							</Tr>
						))}
					</Tbody>
				</Table>
			</StackItem>
		</>
	);

	const renderFutureVersionIndicators = () => {
		if (!isRhelFutureValidated && RHOCPFutureVersionCount === 0) return null;
		return (
			<StackItem>
				<Content component={ContentVariants.ol} isPlainList style={{ paddingLeft: "2rem" }}>
					{isRhelFutureValidated && (
						<Content component="li">
							<Icon isInline status="success">
								<CheckCircleIcon />
							</Icon>
							&nbsp;This product supports the latest version of RHEL
						</Content>
					)}
					{RHOCPFutureVersionCount > 0 && (
						<Content component="li">
							<Icon isInline status="success">
								<CheckCircleIcon />
							</Icon>
							&nbsp;This product supports next {RHOCPFutureVersionCount} minor version
							{RHOCPFutureVersionCount > 1 ? "s" : ""} of Red Hat OpenShift
						</Content>
					)}
				</Content>
			</StackItem>
		);
	};

	const validationTypeSelector = (options: IRadioItem[]) => (
		<Formik
			initialValues={{ [VALIDATION_TYPE_FIELD]: "" }}
			onSubmit={(values) => setSelectedWorkloadType(values[VALIDATION_TYPE_FIELD] as EWorkloadType)}
		>
			{({ values, setFieldValue, handleSubmit }) => (
				<>
					<StackItem>
						<ActionList>
							<ActionListGroup>
								<ActionListItem>
									<Button variant="secondary" component={(props) => <Link {...props} to={versionMatrixLink} />}>
										Update version matrix
									</Button>
								</ActionListItem>
								<ActionListItem>
									<Button
										variant={partnerValidationProgress === EPartnerValidationProgress.Started ? "primary" : "secondary"}
										onClick={onNext}
									>
										Next
									</Button>
								</ActionListItem>
							</ActionListGroup>
						</ActionList>
					</StackItem>
					<StackItem>
						<Form>
							<FormGroup
								label="Validation type"
								isRequired
								fieldId="validation-type"
								labelHelp={
									<ToolTip
										buttonAriaLabel="More info about validation types"
										buttonContent={<OutlinedQuestionCircleIcon />}
									>
										Select the validation type that best describes how your product is deployed on Red Hat platforms.
									</ToolTip>
								}
							>
								{options.map((option) => (
									<Radio
										key={option.value}
										id={`validation-type-${option.value}`}
										name={VALIDATION_TYPE_FIELD}
										label={option.name}
										value={option.value}
										isChecked={values[VALIDATION_TYPE_FIELD] === option.value}
										onChange={() => setFieldValue(VALIDATION_TYPE_FIELD, option.value)}
									/>
								))}
							</FormGroup>
						</Form>
					</StackItem>
					<StackItem>
						<Alert
							variant="warning"
							isInline
							isPlain
							title="Once questionnaire is submitted, this product's validation type cannot be changed."
						/>
					</StackItem>
					<StackItem>
						<Button variant="primary" isDisabled={!values[VALIDATION_TYPE_FIELD]} onClick={() => handleSubmit()}>
							Start questionnaire
						</Button>
					</StackItem>
				</>
			)}
		</Formik>
	);

	const renderValidationTypeFlow = (options: IRadioItem[]) => {
		const workloadType = persistedWorkloadType ?? selectedWorkloadType;
		const lockedOption = workloadType ? options.find((option) => option.value === workloadType) : undefined;
		return (
			<>
				{lockedOption ? (
					<>
						<StackItem>
							<Content>
								<Content component="p">
									<strong>Validation type:</strong> {lockedOption.name}
								</Content>
							</Content>
						</StackItem>
						{renderQuestionnaireSection(WorkloadTypeQuestionnaireAlias[lockedOption.value as EWorkloadType])}
					</>
				) : (
					validationTypeSelector(options)
				)}
			</>
		);
	};

	const validationInProgressState = () => (
		<>
			{isProductCertified && (rhelVersionsSupported.length > 0 || rhOCPVersionsSupported.length > 0) ? (
				<StackItem>
					<Alert
						variant={"warning"}
						title={"Support versions set by certification"}
						actionLinks={
							<AlertActionLink
								component="a"
								onClick={() => {
									navigateToComponentTesting();
								}}
							>
								Go to certification tests
							</AlertActionLink>
						}
						isInline
					>
						To expand your support statement to other Red Hat platform versions, complete the relevant certification
						tests.
					</Alert>
				</StackItem>
			) : productDetail.type === ProductListingTypes.ContainerStack ? (
				renderValidationTypeFlow(ContainerValidationTypeOptions)
			) : productDetail.type === ProductListingTypes.TraditionalApplication ? (
				renderValidationTypeFlow(StandaloneValidationTypeOptions)
			) : null}
		</>
	);

	const validatedState = () => (
		<>
			{hasOverflowRows && (
				<StackItem>
					<Alert
						variant="info"
						isInline
						title={`You have more than  ${SUPPORTED_PLATFORMS_MAX_ROWS} product versions.`}
					>
						visit the{" "}
						<Button isInline variant="link" component={(props) => <Link {...props} to={versionMatrixLink} />}>
							version matrix
						</Button>{" "}
						to view all of your product versions.
					</Alert>
				</StackItem>
			)}
			<StackItem>
				<Content>
					<Content component="h2">Support statement</Content>
				</Content>
			</StackItem>
			<StackItem>
				<Content>
					<Icon isInline status="success">
						<CheckCircleIcon />
					</Icon>
					&nbsp;This product has been validated
				</Content>
			</StackItem>
			{hasVersionMatrixData && (
				<>
					{vendorMatrixTable()}
					{renderFutureVersionIndicators()}
				</>
			)}
			<StackItem>
				<Button variant="primary" component={(props) => <Link {...props} to={versionMatrixLink} />}>
					Update version matrix
				</Button>
			</StackItem>
		</>
	);

	const renderSupportStatementCopy = () => {
		if (partnerValidationProgress === EPartnerValidationProgress.Validated) {
			return null;
		}
		if (partnerValidationProgress !== EPartnerValidationProgress.None) {
			return vendorMatrixTable();
		} else
			return (
				<>
					<StackItem>
						<Content>
							<Content component="h2">Support statement</Content>
							<Content component="p">
								Report supported Red Hat versions to validate your containerized product. Validation completes after Red
								Hat approval.
							</Content>
							<Content component="p">
								This support statement is intended to reflect the most recent version of your product at the time of
								publication to the Ecosystem Catalog. If you would like the Red Hat Ecosystem Catalog to display which
								other versions of your product support specific Red Hat platforms, you can fill out the{" "}
								<Button isInline variant="link" component={(props) => <Link {...props} to={versionMatrixLink} />}>
									version matrix
								</Button>{" "}
								for your product. Version matrix entries are not verified by Red Hat and will not count toward
								Validation or Certification requirements for publishing.
							</Content>
						</Content>
					</StackItem>
					<StackItem>
						<ActionList>
							<ActionListGroup>
								<ActionListItem>
									<Button
										isDisabled={globalDisabledState || isReadOnly}
										variant={"primary"}
										onClick={() => handleProductVersionModal(true)}
									>
										{"Start validation"}
									</Button>
								</ActionListItem>
								<ActionListItem>
									<Button variant={"secondary"} onClick={onNext}>
										Next
									</Button>
								</ActionListItem>
							</ActionListGroup>
						</ActionList>
					</StackItem>
				</>
			);
	};
	return (
		<Stack hasGutter={true}>
			{renderSupportStatementCopy()}
			{partnerValidationProgress === EPartnerValidationProgress.Validated && validatedState()}
			{partnerValidationProgress === EPartnerValidationProgress.Started && validationInProgressState()}
			<ProductVersionModal
				handleProductVersionModal={handleProductVersionModal}
				modalState={productVersionModalState}
			/>
		</Stack>
	);
};

export default SupportStatement;
